// Registers the service worker built from src/service-worker.js. Only runs
// in production builds — `npm start` never registers it, so local dev/testing
// is unaffected.

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config) {
  if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log("This web app is being served cache-first by a service worker.");
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

// Switches a downloaded-but-waiting new version in and reloads onto it.
// Only meant for moments where nobody is mid-task (a fresh launch, or the
// app coming back from the background) — a visible session gets the banner
// instead. `controllerchange` also fires the very first time a worker claims
// a page that had none, which needs no reload, hence the controller check.
let applying = false;

// Pages where a surprise reload would throw away something the person is in
// the middle of (an OTP signup they left the app to fetch the code for, a
// photo picked for an upload) — there the update waits for the banner tap.
function isBusyPage() {
  const path = window.location.pathname;
  return path.startsWith("/login") || path.startsWith("/upload");
}

function applyWaitingUpdate(registration) {
  const waiting = registration && registration.waiting;
  if (applying || !waiting || !navigator.serviceWorker.controller) return;
  applying = true;
  navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
  waiting.postMessage({ type: "SKIP_WAITING" });
  // If activation never completes, let a later resume try again.
  setTimeout(() => {
    applying = false;
  }, 8000);
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // A new version that finished downloading in an EARLIER visit (say, in
      // Chrome) is already "waiting" by the time this page loads, so
      // `onupdatefound` below never fires for it — an installed app opened
      // afterwards would sit on the old version, with no banner, until every
      // window was closed. Pick it up here: this is a fresh launch, so switch
      // straight to it.
      if (registration.waiting && navigator.serviceWorker.controller) {
        if (isBusyPage()) {
          if (config && config.onUpdate) config.onUpdate(registration);
        } else {
          applyWaitingUpdate(registration);
        }
      }

      // An installed app resumed from the background never navigates, so the
      // browser never re-checks for a new version on its own. Check whenever
      // it comes back to the foreground (and hourly while it stays open), and
      // apply anything already downloaded at that same moment.
      const checkForUpdate = () => registration.update().catch(() => {});
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState !== "visible") return;
        if (registration.waiting && navigator.serviceWorker.controller) {
          if (isBusyPage()) {
            if (config && config.onUpdate) config.onUpdate(registration);
          } else {
            applyWaitingUpdate(registration);
          }
        }
        checkForUpdate();
      });
      setInterval(checkForUpdate, 60 * 60 * 1000);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              console.log("New content is available.");
              // Downloaded while the app was in the background: nobody is
              // looking, so switch now rather than asking later.
              if (document.visibilityState === "hidden" && !isBusyPage()) {
                applyWaitingUpdate(registration);
              } else if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              console.log("Content is cached for offline use.");
              if (config && config.onSuccess) config.onSuccess(registration);
            }
          }
        };
      };
    })
    .catch(error => {
      console.error("Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then(response => {
      const contentType = response.headers.get("content-type");
      if (response.status === 404 || (contentType != null && contentType.indexOf("javascript") === -1)) {
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log("No internet connection found. App is running in offline mode.");
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then(registration => registration.unregister())
      .catch(error => console.error(error.message));
  }
}
